import React from 'react';
import PropTypes from 'prop-types';

// Import JS Modules
import { builderDispatch, WebUI, PrismUI } from '../_utils/ui';
import get from 'lodash/get';
import ShelfEvents from '../_shelf-category/flux-events';
import QuantityPricingTools from './quantity-pricing-tools';
import CalendarDates from './calendar-dates';
// Import JSX Modules
import GroupedCategoryBase from './_common/grouped-category-base';
import DeliveryOptionCalculator from '../_utils/_delivery-options/calculator';
import GroupedCategoryChangeEvents from './grouped-category-change-events';
import CalendarStartDateSelector from './calendar-start-date-selector';
import DeliveryOptions from './delivery-options';
import Designer from './designer';
import Filters from './filters';
import ButtonOptionSelectors from './button-option-selectors';
import OptionSelectors from './option-selectors';
import ProductDescription from './product-description';
import ProductImages from './product-images';
import ProductNotAvailable from './product-not-available';
import Pickup from './pickup';
import Shipping from './shipping';
import Breadcrumb from '../_utils/breadcrumb';
import DeliveryOptionsList from '../_utils/_delivery-options/delivery-options-list';
import MobileFullScreen from '../_utils/mobile-full-screen';
import Favouritable from '../_utils/favouritable';
import {
  removeInlineSeo,
  shouldDisplayTheme,
} from '../_utils/tools/seo-helpers';
import ProductFlags from './product-flags';
import CrossSellCollection from './_common/cross-sell/cross-sell-collection';
import DeliverySection from '../_grouped-category/delivery-section';
import StockAvailability from '../_grouped-category/stock-availability';
import ViewAllModal             from './view-all-modal';
import classNames from 'classnames';
import Variants from './variants';

// Main Section
class GroupedCategory extends GroupedCategoryBase {
  constructor(props) {
    super(props);
    const {
      favourited,
      data: { delivery_options },
      selected_delivery_options: selectedPropDeliveryOptions,
      variant
    } = this.props;
    this.state = Object.assign(this.state, {
      leftArrowPosition: '20px',
      calendarStartDate: CalendarDates.getDefaultValue().value,
      favourited,
      pickupOpen: false,
      shippingOpen: false,
      selectedDeliveryOptionsState: selectedPropDeliveryOptions || String(delivery_options[0].key),
      selectedVariant: variant,
    });
  }

  componentDidMount() {
    super.componentDidMount();
    this.builderDispatchToken = builderDispatch.register((payload) => {
      switch (payload.type) {
        case WebUI.LOGIN:
          if (!payload.previous_basket_contents) {
            this.handleReload();
          }
          break;
        case WebUI.CART_MERGED:
          this.handleReload();
          break;
        case ShelfEvents.FAVOURITED:
          this.handleFavourited();
          break;
        case PrismUI.EVENT_QUANTITY_CHANGED:
          this.setState({
            copies: payload.value,
            quantityChanged: true,
          });
          break;
      }
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handleClosePickupShipping);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', this.handlePopState);
      window.addEventListener('pageshow', this.handlePageShow);
    }

  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.handleClosePickupShipping);
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('popstate', this.handlePopState);
      window.removeEventListener('pageshow', this.handlePageShow);
    }
  }

  getChildContext() {
    const {
      data: {
        description,
        show_prices_table: showPricesTable,
        use_icons_not_text_in_filters: useIconsNotTextInFilters,
        use_icons_in_comparison_fields: useIconsInComparisonFields,
      },
      urls: { locations: locationsUrl },
    } = this.props,
      { validateOptionsChosen } = this.state;

    return {
      description,
      showPricesTable,
      useIconsNotTextInFilters,
      useIconsInComparisonFields,
      validateOptionsChosen,
      locationsUrl,
    };
  }

  render() {
    const {
      data: {
        title,
        filters,
        delivery_options: deliveryOptions,
        breadcrumbs,
        prints_item_page: printsItemPage,
      },
      design_code: defaultDesignCode,
      features: {
        change_delivery_opts_before_checkout: changeDeliveryOptsBeforeCheckout,
      },
      item_page_insert: itemPageInsert,
      grouped_category_new_ui: groupedCategoryNewUi,
      check_stock_availability: checkStockAvailability
    } = this.props;
    let {
      data: { products },
    } = this.props;

    const filteredDeliveryOptions = deliveryOptions.filter((option) => {
      const isPresent = products.some((product) =>
        product.delivery_options.includes(option.key),
      );
      return isPresent;
    });

    const variantName = () => {
      if (!selectedTheme) return;
      const {selectedVariant} = this.state;
      const {variants} = selectedTheme;

      if(!variants) return {};

      return selectedVariant in variants ? 
             selectedVariant : 
             Object.keys(variants).find((key) => variants[key].default);
    };

    if (products.length === 0) {
      return (
        <div className="alert alert-warning" role="alert">
          <strong>Sorry:</strong> No products were found.
        </div>
      );
    }

    removeInlineSeo();

    const currentProducts = this.getCurrentProducts(),
      [defaultProduct] = currentProducts.length ? currentProducts : products,
      [selectedTheme] = defaultProduct.themes;

    let $priceAndCreate,
      $designer,
      $page,
      currentProduct,
      $itemPageInsert,
      { copies, quantityChanged } = this.state;

    if (defaultProduct.default_quantity && !quantityChanged) {
      copies = defaultProduct.default_quantity;
    }

    if (
      defaultDesignCode &&
      selectedTheme &&
      selectedTheme.designers_names &&
      selectedTheme.designers_names[0]
    ) {
      $designer = (
        <Designer
          name={selectedTheme.designers_names[0]}
          url={breadcrumbs[breadcrumbs.length - 2].url}
        />
      );
    }

    if (currentProducts.length === 1) {
      const {selectedVariant, selectedDeliveryOptionsState} = this.state;
      [currentProduct] = currentProducts;
      currentProduct.price = currentProduct.minimum_price;

      if (
        selectedTheme &&
        selectedTheme.designers_names &&
        selectedTheme.designers_names[0]
      ) {
        $designer = (
          <Designer
            name={selectedTheme.designers_names[0]}
            url={breadcrumbs[breadcrumbs.length - 2].url}
          />
        );
      }

      this.setPageAttributesForProduct(
        currentProduct,
        selectedTheme,
        defaultDesignCode,
        selectedVariant,
        selectedDeliveryOptionsState,
      );

      $page = this.setInnerHtmlDescription(currentProduct.page_html);

      copies = (() => {
        const quantityPricingTools = new QuantityPricingTools(currentProduct);
        return quantityPricingTools.currentOrFirstAvailableCopies(copies);
      })();

      if (
        get(this.productSelectedAnalyticsSentFor, 'product') !==
        currentProduct.id ||
        get(this.productSelectedAnalyticsSentFor, 'copies') !== copies
      ) {
        builderDispatch.dispatch({
          type: GroupedCategoryChangeEvents.PRODUCT_SELECTED,
          product: currentProduct,
          deliveryOptions,
          breadcrumbs,
          copies,
          printsItemPage,
        });

        this.productSelectedAnalyticsSentFor = {
          product: currentProduct.id,
          copies,
        };
      }
    }

    if (currentProducts.length === products.length) {
      if (!this.landingAnalyticsSent) {
        builderDispatch.dispatch({
          type: GroupedCategoryChangeEvents.PAGE_LOADED,
          product: currentProducts[0],
          deliveryOptions,
          breadcrumbs,
          printsItemPage,
        });

        this.landingAnalyticsSent = true;
      }
    }

    const {
      selectedDeliveryOptionsState,
      calendarStartDate,
      options
    } = this.state;
    $priceAndCreate = this.renderPriceAndProductCreate(
      currentProducts,
      selectedTheme,
      calendarStartDate,
      copies,
      options,
      variantName(),
      selectedDeliveryOptionsState,
      groupedCategoryNewUi,
    );
    $itemPageInsert = (
      <div dangerouslySetInnerHTML={{ __html: itemPageInsert }} />
    );

    let $deliveryOptions,
      $deliveryOptionsDropdowns = [];
    if (changeDeliveryOptsBeforeCheckout) {
      $deliveryOptions = (
        <DeliveryOptionsList
          options={deliveryOptions}
          product={currentProduct}
          selectedDeliveryOptions={selectedDeliveryOptionsState}
        />
      );
    } else {
      $deliveryOptions = (
        <DeliveryOptions
          options={deliveryOptions}
          products={currentProducts}
          pickupOpen={this.state.pickupOpen}
          shippingOpen={this.state.shippingOpen}
          handleOpenPickup={this.handleOpenPickup}
          handleOpenShipping={this.handleOpenShipping}
        />
      );

      let productsDeliveryOptions = [];
      for (const product of products) {
        productsDeliveryOptions = productsDeliveryOptions.concat(
          product.delivery_options,
        );
      }
      const { hasOneHourPickup } =
        DeliveryOptionCalculator.productsDeliveryOptionsTiers(
          productsDeliveryOptions,
          deliveryOptions,
        );

      $deliveryOptionsDropdowns = [
        <Pickup
          key="pickup"
          deliveryTimes={deliveryTimes(
            (deliveryOption) => deliveryOption.type === 'PICKUP',
          )}
          hasOneHourPickup={hasOneHourPickup}
          open={this.state.pickupOpen}
          disabled={this.state.pickupDisabled}
          leftArrowPosition={this.state.leftArrowPosition}
        />,
        <Shipping
          key="shipping"
          deliveryTimes={deliveryTimes(
            (deliveryOption) => deliveryOption.type !== 'PICKUP',
          )}
          open={this.state.shippingOpen}
          disabled={this.state.shippingDisabled}
          leftArrowPosition={this.state.leftArrowPosition}
        />,
      ];
    }

    const handleDeliverySelection = (option) => {
      this.setState({
        selectedDeliveryOptionsState: option.toString(),
      });
    };

    const $favoritable = (() => {
      if (typeof App !== 'undefined' && App.order.logged_in()) {
        return (
          <Favouritable
            designCode={defaultDesignCode}
            favourited={this.state.favourited}
          />
        );
      }
    })();

    const itemThemeTitle =
      (defaultDesignCode && selectedTheme && selectedTheme.title) || title;

    const breadCrumbTitle = () => {
      if (currentProduct) {
        return currentProduct.title;
      } else {
        return shouldDisplayTheme() ? `${title} - ${itemThemeTitle}` : title;
      }
    };

    const {selectedVariant} = this.state;
    return (
      <div>
        <Breadcrumb segments={breadcrumbs} title={breadCrumbTitle()} />
        <p className="item-theme-title">
          {itemThemeTitle}
          {$favoritable}
        </p>
        {$designer}
        {$page}
        <div className="heading-flags">{currentProduct && renderFlag()}</div>
        <ViewAllModal
          open={this.state.viewAllImages}
          product={defaultProduct}
          key={`${defaultProduct.id}-${selectedVariant}`}
          selectedTheme={selectedTheme}
          rounded_corners={this.roundOptionActive()}
          scalloped_edges={this.scallopedOptionActive()}
          elegant_corners={this.elegantOptionActive()}
          selectedOptions={options}
          productOptions={currentProduct && currentProduct.options}
          viewAll={this.handleViewAllProductImages}
          selectedVariant={selectedVariant}
          onClose={() => this.handleViewAllProductImages(false)}          
        />
        <div className="row mt-5">
          <div className="col-12 col-md-6 product-images">
            <ProductImages
              product={defaultProduct}
              key={`${defaultProduct.id}-${selectedVariant}`}
              selectedTheme={selectedTheme}
              rounded_corners={this.roundOptionActive()}
              scalloped_edges={this.scallopedOptionActive()}
              elegant_corners={this.elegantOptionActive()}
              selectedOptions={options}
              productOptions={currentProduct && currentProduct.options}
              viewAll={this.handleViewAllProductImages}
              selectedVariant={selectedVariant}
            />
          </div>
          {groupedCategoryNewUi ? (
            <div className="col-12 col-md-6 product-details mt-5 mt-md-0 mt-lg-0">
              <div
                className="card filters-and-product"
                ref={this.productAndFilters}>
                <div className="card-header filters">
                  <DeliverySection
                    deliveryOptions={filteredDeliveryOptions}
                    selectedDeliveryOptions={
                      selectedDeliveryOptionsState
                    }
                    handleDeliveryOptions={handleDeliverySelection}
                    products={products}
                  />
                </div>
                <div className="new-card-body filters product">
                  <Variants
                    variants={selectedTheme && selectedTheme.variants}
                    selectedVariant={selectedVariant}
                    onVariantSelected={this.handleChangeVariant}
                  />
                  <Filters
                    levels={filters}
                    selected={this.state.filters}
                    products={products}
                    currentProducts={currentProducts}
                    deliveryOptions={deliveryOptions}
                    onCriteronSelected={this.handleFilterCriteronSelected}
                    selectedDeliveryOptions={
                      this.state.selectedDeliveryOptionsState
                    }
                  />
                  {renderCalendarSelector.call(this)}
                  {this.props.show_button_for_frames ? (
                    <ButtonOptionSelectors
                      options={currentProduct && currentProduct.options}
                      selected={this.state.options}
                      onChange={this.handleOptionChange}
                    />
                  ) : (
                    <OptionSelectors
                      options={currentProduct && currentProduct.options}
                      selected={this.state.options}
                      onChange={this.handleOptionChange}
                    />
                  )}
                  {checkStockAvailability && currentProducts && (
                    <StockAvailability
                      currentProducts={currentProducts}
                      selectedDeliveryOption={this.state.selectedDeliveryOptionsState} />
                  )}
                  <div className="product-selection">
                    <div className="price-and-create-mobile d-block d-sm-none">
                      <ProductNotAvailable
                        visible={currentProducts.length === 0}
                      />
                      {$priceAndCreate}
                    </div>

                    <div className="row d-md-block align-self-end">
                      <ProductNotAvailable
                        visible={currentProducts.length === 0}
                      />
                      {$priceAndCreate}
                    </div>
                  </div>
                  <div>{$itemPageInsert}</div>
                </div>
              </div>
              {this.renderCrossSellCollection(
                currentProducts.length ? currentProducts : products,
                selectedTheme,
              )}
            </div>
          ) : (
            <div className="col-12 col-md-6 product-details mt-5 mt-lg-0">
              <div
                className="card filters-and-product"
                ref={this.productAndFilters}>
                <div className="card-header filters">
                  <Variants
                    variants={selectedTheme && selectedTheme.variants}
                    selectedVariant={selectedVariant}
                    onVariantSelected={this.handleChangeVariant}
                  />
                  <Filters
                    levels={filters}
                    selected={this.state.filters}
                    products={products}
                    currentProducts={currentProducts}
                    deliveryOptions={deliveryOptions}
                    onCriteronSelected={this.handleFilterCriteronSelected}
                  />
                  {renderCalendarSelector.call(this)}
                  {this.props.show_button_for_frames ? (
                    <ButtonOptionSelectors
                      options={currentProduct && currentProduct.options}
                      selected={this.state.options}
                      onChange={this.handleOptionChange}
                    />
                  ) : (
                    <OptionSelectors
                      options={currentProduct && currentProduct.options}
                      selected={this.state.options}
                      onChange={this.handleOptionChange}
                    />
                  )}
                </div>
                <div className="card-body product">
                  <div className="d-lg-none">
                    <ProductNotAvailable
                      visible={currentProducts.length === 0}
                    />
                    {changeDeliveryOptsBeforeCheckout && $deliveryOptions}
                    {$priceAndCreate}
                    {changeDeliveryOptsBeforeCheckout || $deliveryOptions}
                  </div>
                  <div className="row d-none d-lg-flex">
                    <ProductNotAvailable
                      visible={currentProducts.length === 0}
                    />
                    <div className="col-6 col-xl-8 align-self-end">
                      {$deliveryOptions}
                    </div>
                    <div className="col-6 col-xl-4 align-self-end">
                      {$priceAndCreate}
                    </div>
                  </div>
                </div>
                <div>{$itemPageInsert}</div>
                {$deliveryOptionsDropdowns}
              </div>
              {this.renderCrossSellCollection(
                currentProducts.length ? currentProducts : products,
                selectedTheme,
              )}
            </div>
          )}
        </div>

        <ProductDescription
          currentProduct={currentProduct}
          products={products}
          deliveryOptions={deliveryOptions}
        />

        <MobileFullScreen
          smallScreenOnly
          open={this.state.pickupOpen}
          onBack={this.handleClosePickupShipping}>
          {$deliveryOptionsDropdowns[0]}
        </MobileFullScreen>
        <MobileFullScreen
          smallScreenOnly
          open={this.state.shippingOpen}
          onBack={this.handleClosePickupShipping}>
          {$deliveryOptionsDropdowns[1]}
        </MobileFullScreen>
      </div>
    );
    function renderFlag() {
      const props = {};

      if (currentProduct) {
        props.prices = currentProduct.prices;
        props.was_price = currentProduct.was_price;
        props.flag =
          (currentProduct.flag || 0) + ((selectedTheme || {}).flag || 0);
      }

      return <ProductFlags className="right" {...props} copies={copies} />;
    }

    function renderCalendarSelector() {
      if (defaultProduct.is_calendar) {
        return (
          <CalendarStartDateSelector
            value={this.state.calendarStartDate}
            onChange={this.handleCalendarStartChange}
          />
        );
      }
    }

    function deliveryTimes(predicate) {
      const deliveryDays = {};

      for (const currentProduct of currentProducts) {
        for (const deliveryKey of currentProduct.delivery_options) {
          const deliveryOption = deliveryOptions.find(
            (deliveryOpt) => deliveryOpt.key == deliveryKey,
          );
          if (predicate(deliveryOption)) {
            deliveryDays[deliveryOption.type] = deliveryOption.time_hours;
          }
        }
      }

      return deliveryDays;
    }
  }

  handleChangeVariant = (selectedVariant) => {
    const {design_code: designCode} = this.props;
    const {selectedDeliveryOptionsState} = this.state;
    const currentProducts = this.getCurrentProducts(),
      [defaultProduct] = currentProducts.length ? currentProducts : this.props.data.products,
      [selectedTheme] = defaultProduct.themes;
    const currentProduct = (currentProducts.length === 1) ? currentProducts[0] : defaultProduct;
    this.setPageAttributesForProduct(
      currentProduct,
      selectedTheme,
      designCode,
      selectedVariant,
      selectedDeliveryOptionsState,
    );
    this.setState({selectedVariant});
  };

  handleViewAllProductImages = (state) => {
    this.setState({
      viewAllImages: state
    });
  };

  handleReload() {
    fetch('/favourites', {
      credentials: 'same-origin',
    })
      .then((response) => response.json())
      .then((favourites) => {
        this.setState({
          favourited: favourites.includes(this.props.design_code),
        });
      })
      .catch(() => { });
  }

  handleFavourited() {
    this.setState((prevState) => {
      return {
        favourited: !prevState.favourited,
      };
    });
  }

  handleCalendarStartChange = (timestamp) => {
    this.setState({
      calendarStartDate: timestamp,
    });
  };

  handleOpenPickup = (element, disabled) => {
    this.setState({
      leftArrowPosition: this.calculateLeftArrowPosition(element),
      pickupOpen: !this.state.pickupOpen,
      pickupDisabled: !!disabled,
      shippingOpen: false,
    });
  };

  handleOpenShipping = (element, disabled) => {
    this.setState({
      leftArrowPosition: this.calculateLeftArrowPosition(element),
      pickupOpen: false,
      shippingOpen: !this.state.shippingOpen,
      shippingDisabled: !!disabled,
    });
  };

  handleClosePickupShipping = (e) => {
    if (!this.state.pickupOpen && !this.state.shippingOpen) return;

    if (
      e &&
      $(e.target).closest('.delivery-info, .delivery-box, .mobile-full-screen')
        .length > 0
    ) {
      return;
    }

    this.setState({
      pickupOpen: false,
      shippingOpen: false,
    });
  };

  calculateLeftArrowPosition = (element) => {
    // Calculate the left space to position the arrow in the middle of the delivery-box location message
    let $element = $(element),
      $locationMessage = $element
        .closest('.delivery-box')
        .find('.see-locations-message'),
      leftArrowPosition;

    leftArrowPosition =
      $locationMessage.offset().left +
      $locationMessage.width() / 2 -
      $element.closest('.product').offset().left -
      12.7;

    return `${leftArrowPosition}px`;
  };

  renderCrossSellCollection = (products, theme) => {
    const { collections } = this.props.data;

    if (!collections || !products || !theme) {
      return null;
    }

    const collectionToDisplay = collections.filter(
      (collectionProduct) =>
        !products.some(
          (product) => collectionProduct.product_id === product.id,
        ) && // do not display products from the same group
        collectionProduct.thumbnails &&
        collectionProduct.thumbnails.length > 0 &&
        collectionProduct.unit_prices &&
        collectionProduct.unit_prices.length,
    );

    if (collectionToDisplay.length === 0) {
      return null;
    }

    return (
      <CrossSellCollection
        id="crossSellCollection"
        collection={collectionToDisplay}
        slidesToShow={3}
      />
    );
  };

  /* eslint-disable camelcase */
  roundOptionActive() {
    let found_active_round_option = false,
      rounded_corners_option,
      filter;

    for (const selected_filter_key in this.state.filters) {
      rounded_corners_option = null;

      for (const filter of this.props.data.filters) {
        if (
          filter.title === selected_filter_key &&
          !!filter.rounded_corners_option
        ) {
          rounded_corners_option = filter.rounded_corners_option;

          break;
        }
      }

      if (
        !!rounded_corners_option &&
        this.state.filters[selected_filter_key] === rounded_corners_option
      ) {
        found_active_round_option = true;

        break;
      }
    }
    return found_active_round_option;
  }

  scallopedOptionActive() {
    return this.props.data.filters.some(
      (f) =>
        !!f.scalloped_edges_option &&
        f.scalloped_edges_option === this.state.filters[f.title],
    );
  }

  elegantOptionActive() {
    return this.props.data.filters.some(
      (f) =>
        !!f.elegant_corners_option &&
        f.elegant_corners_option === this.state.filters[f.title],
    );
  }
}

GroupedCategory.childContextTypes = {
  description: PropTypes.string,
  showPricesTable: PropTypes.bool,
  useIconsNotTextInFilters: PropTypes.bool,
  useIconsInComparisonFields: PropTypes.bool,
  validateOptionsChosen: PropTypes.bool,
  locationsUrl: PropTypes.string,
  groupedCategoryNewUi: PropTypes.bool,
};

export default GroupedCategory;
